import { Link, useParams } from "react-router-dom";
import { Clock, ArrowRight } from "lucide-react";
import Seo from "../components/Seo";
import ArticleCard, { formatDate } from "../components/ArticleCard";
import FaqAccordion from "../components/FaqAccordion";
import CtaBand from "../components/CtaBand";
import NotFound from "./NotFound";
import { Reveal, Eyebrow, ChapterHeading } from "../components/Reveal";
import { SITE, getService, getLocation } from "../data/site";
import { getArticle, relatedArticles } from "../data/articles";

export default function ArticlePage() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <NotFound />;

  const related = relatedArticles(article, 2);
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.description,
    datePublished: article.date,
    author: { "@type": "Organization", name: SITE.fullName },
    publisher: {
      "@type": "Organization",
      name: SITE.fullName,
      url: SITE.domain,
      logo: { "@type": "ImageObject", url: `${SITE.domain}/og-image.png` },
    },
    mainEntityOfPage: `${SITE.domain}/resources/${article.slug}`,
  };

  return (
    <>
      <Seo
        title={`${article.title} | Xtreme Protection England`}
        description={article.description}
        canonical={`${SITE.domain}/resources/${article.slug}`}
        jsonLd={jsonLd}
      />

      <section className="tactical-grid border-b border-white/10" data-testid="article-hero">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-20">
          <nav className="font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-8" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-amber transition-colors">Home</Link>
            <span className="mx-2 text-amber">//</span>
            <Link to="/resources" className="hover:text-amber transition-colors">Resources</Link>
            <span className="mx-2 text-amber">//</span>
            <span className="text-white">{article.category}</span>
          </nav>
          <Reveal>
            <Eyebrow>{article.category} // The Intel Desk</Eyebrow>
            <h1 className="font-display font-black uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl text-white">
              {article.title}
            </h1>
            <div className="mt-6 flex items-center gap-4 font-mono text-[10px] uppercase tracking-widest text-gray-500">
              <span>{formatDate(article.date)}</span>
              <span className="flex items-center gap-1.5">
                <Clock className="w-3 h-3 text-amber" /> {article.readMins} min read
              </span>
              <span>By {SITE.name}</span>
            </div>
          </Reveal>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-5 sm:px-8 py-14 sm:py-16" data-testid="article-body">
        {article.intro.map((p, i) => (
          <p key={i} className="text-base sm:text-lg text-gray-300 leading-relaxed mb-6">
            {p}
          </p>
        ))}
        {article.sections.map((s, i) => (
          <section key={i} className="mt-10">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight mb-5">
              {s.h}
            </h2>
            {s.paras.map((p, j) => (
              <p key={j} className="text-sm sm:text-base text-gray-400 leading-relaxed mb-5">
                {p}
              </p>
            ))}
          </section>
        ))}

        <section className="mt-12 border border-amber/30 bg-coal/40 p-6" data-testid="article-service-links">
          <p className="font-mono text-[10px] uppercase tracking-widest text-amber mb-4">
            Related services & coverage
          </p>
          <div className="flex flex-wrap gap-2">
            {article.relatedServices.map((s) => {
              const svc = getService(s);
              return svc ? (
                <Link
                  key={s}
                  to={`/services/${s}`}
                  className="font-mono text-[10px] uppercase tracking-widest text-white border border-white/15 px-3 py-2 hover:border-amber hover:text-amber transition-colors"
                >
                  {svc.title}
                </Link>
              ) : null;
            })}
            {article.relatedCities.map((c) => {
              const loc = getLocation(c);
              return loc ? (
                <Link
                  key={c}
                  to={`/locations/${c}`}
                  className="font-mono text-[10px] uppercase tracking-widest text-white border border-white/15 px-3 py-2 hover:border-amber hover:text-amber transition-colors"
                >
                  Security {loc.name}
                </Link>
              ) : null;
            })}
          </div>
        </section>

        <section className="mt-12" data-testid="article-faq">
          <ChapterHeading number="//" label="QUESTIONS" />
          <FaqAccordion faqs={article.faqs} testidPrefix="article-faq" />
        </section>
      </article>

      <section className="border-t border-white/10 bg-coal/30 py-14 sm:py-16" data-testid="article-related">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between gap-6 mb-8">
            <h2 className="font-display font-extrabold uppercase tracking-tight text-2xl sm:text-3xl text-white leading-tight">
              Keep reading
            </h2>
            <Link
              to="/resources"
              className="group hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-amber hover:text-white transition-colors"
            >
              All guides
              <ArrowRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-4xl">
            {related.map((a) => (
              <ArticleCard key={a.slug} article={a} prefix="related-article" />
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Ready to secure your site?"
        text={`Free risk assessment anywhere in England. Call ${SITE.phone} or email ${SITE.email} — the command centre answers 24/7.`}
      />
    </>
  );
}
